// Imports
import { saveBlobAsFile } from '@/api/apiUtils'
import { DownloadIcon, SearchIconBtn } from '@/assets/icons'
import { Search } from '@/components/common'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import CustomButton from '@/components/Returns/CustomButton'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import { MT_ENTITY_COLUMNS } from '@/multi-territory/mt-configs/mt-entity'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import {
  deleteEntityMaster,
  downloadEntityMaster,
  getEntityMasterList,
  MTEntityItem,
  MTEntityListRequest,
  MTEntitySaveRequest,
  MTEntityUpdateRequest,
  saveEntityMaster,
  updateEntityMaster,
} from '@/multi-territory/mt-services/mt-entity-api'
import type { MTCountryOption } from '@/multi-territory/mt-types/mt-admin'
import type { MTMetadataTableColumn } from '@/multi-territory/mt-types/mt-metadata'
import {
  buildMTSearchField,
  formatMTHeaderLabel,
  formatMTSearchCriteria,
} from '@/multi-territory/mt-utils/mt-search-fields'
import { ROLE_CAPABILITIES } from '@/utils/role-capabilities'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { MTAdminEntityForm } from './MTAdminEntityForm'

const EntitySearch = Search as any

// Constants

export function MTAdminEntity() {
  // Hooks
  const roleTypes = useSelector((state: any) => state.user.role)
  const reduxCountryOptions: MTCountryOption[] =
    useSelector((state: any) => state.navigation.countryOptions) ||
    COUNTRY_OPTIONS

  // State
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [editingEntity, setEditingEntity] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entityToDelete, setEntityToDelete] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [entityData, setEntityData] = useState<MTEntityItem[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: MT_ENTITY_COLUMNS[0]?.id || '',
      value: '',
    },
  ])

  const { showSnackbar } = useSnackbar()

  // Derived Data
  const roleConfig =
    ROLE_CAPABILITIES[roleTypes as keyof typeof ROLE_CAPABILITIES] ||
    ROLE_CAPABILITIES.default

  // --- Fetch Entity List ---

  const fetchEntityList = useCallback(
    async (searchList: any[] = []) => {
      setLoading(true)
      const payload: MTEntityListRequest = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC',
        },
        searchCOList: searchList,
      }

      const response = await getEntityMasterList(undefined, payload)

      if (response.success) {
        const { content, totalElements } = response.data

        // Map API response to table-compatible format
        const mappedData = content?.map((item: MTEntityItem) => ({
          uuid: item.uuid,
          entityName: item.entityName,
          uniqueIdentificationNumber: item.uniqueIdentificationNumber,
          countryName: item.countryName,
          countryCode: item.countryCode,
          createdBy: item.createdBy ?? '-',
          createdDate: item.createdDate ?? '-',
          // Keys align with the column ids (lastModifiedBy/lastModifiedDate) so
          // the grid renders and the advanced-search fieldName matches the
          // backend allow-list.
          lastModifiedBy: item.lastModifiedBy ?? '-',
          lastModifiedDate: item.lastModifiedDate ?? '-',
        }))
        setEntityData(mappedData as unknown as MTEntityItem[])
        setTotalItems(totalElements)
      }
      setLoading(false)
    },
    [currentPage, itemsPerPage]
  )

  useEffect(() => {
    fetchEntityList()
  }, [fetchEntityList])

  // --- Handlers ---

  const handlePageChange = (page: number): void => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (size: number): void => {
    setItemsPerPage(size)
    setCurrentPage(1)
  }

  const handleEdit = (row: Record<string, unknown>): void => {
    setEditingEntity(row)
    setModalOpen(true)
  }

  const handleDelete = (row: Record<string, unknown>): void => {
    setEntityToDelete(row)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = (): void => {
    setDeleteDialogOpen(false)
    setEntityToDelete(null)
  }

  const confirmDelete = async (): Promise<void> => {
    const uuid = entityToDelete?.uuid as string
    if (!uuid) return

    const response = await deleteEntityMaster(uuid)

    if (response.success) {
      showSnackbar(response.data, 'success')
      setDeleteDialogOpen(false)
      setEntityToDelete(null)
      fetchEntityList()
    }
  }

  const handleSearchOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setSearchAnchorEl(event.currentTarget)
    setSearchOpen(true)
  }

  const handleSearchClose = (): void => {
    setSearchOpen(false)
    setSearchAnchorEl(null)
  }

  const handleApplySearch = (criteria: any[]): void => {
    const apiCriteria = formatMTSearchCriteria(criteria)
    const mappedCriteria = apiCriteria.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: item.id || idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    setSearchOpen(false)
    setSearchAnchorEl(null)
    fetchEntityList(apiCriteria)
  }

  const handleResetSearch = (): void => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: MT_ENTITY_COLUMNS[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    setSearchAnchorEl(null)
    fetchEntityList([])
  }

  const handleAdd = (): void => {
    setEditingEntity(null)
    setModalOpen(true)
  }

  const handleModalClose = (): void => {
    setModalOpen(false)
    setEditingEntity(null)
  }

  const handleFormSubmit = async (formData: {
    entityName: string
    country: string
    uniqueIdentificationNumber: string
    countryUuid?: string
    countryName?: string
  }): Promise<void> => {
    let response

    if (editingEntity) {
      // Update existing entity
      const updatePayload: MTEntityUpdateRequest = {
        uuid: editingEntity.uuid as string,
        entityName: formData.entityName.trim(),
        uniqueIdentificationNumber: formData.uniqueIdentificationNumber,
        countryUuid: formData.countryUuid ?? '',
        countryCode: formData.country,
      }
      response = await updateEntityMaster(updatePayload)
    } else {
      // Create new entity
      const savePayload: MTEntitySaveRequest = {
        entityName: formData.entityName.trim(),
        uniqueIdentificationNumber: formData.uniqueIdentificationNumber,
        countryUuid: formData.countryUuid ?? '',
        countryCode: formData.country,
        countryName: formData.countryName ?? '',
      }
      response = await saveEntityMaster(savePayload)
    }

    if (!response.success) {
      // showSnackbar(response.error, 'error');
      throw new Error(response.error)
    }

    // Success: show snackbar, close modal, refresh list
    showSnackbar(response.data, 'success')
    setModalOpen(false)
    setEditingEntity(null)
    fetchEntityList()
  }

  const handleDownload = async (): Promise<void> => {
    const payload: MTEntityListRequest = {
      pageFilter: {
        currentPage: 1,
        pageSize: totalItems || itemsPerPage,
      },
      searchCOList: [],
    }

    const response = await downloadEntityMaster(payload)

    if (response.success) {
      saveBlobAsFile(response.data, 'entity-master.xlsx')
    } else {
      showSnackbar('Failed to download', 'error')
    }
  }

  // Table columns for MTCommonTable (auto width, capped at 200px, wraps beyond).
  // Append an Action column only when the user can edit.
  const tableColumns = useMemo<MTMetadataTableColumn[]>(() => {
    const base = MT_ENTITY_COLUMNS.map(col => ({
      id: col.id,
      label: col.label,
      type: 'text' as const,
      sortable: false,
      editable: false,
    }))
    if (!roleConfig.canEdit) return base
    return [
      ...base,
      {
        id: '_action',
        label: 'Actions',
        type: 'text' as const,
        sortable: false,
        editable: false,
        width: '120px',
      },
    ]
  }, [roleConfig.canEdit])

  const handleRowEdit = (uuid: string): void => {
    const row = entityData.find(r => (r as any).uuid === uuid)
    if (row) handleEdit(row as unknown as Record<string, unknown>)
  }

  const handleRowDelete = (uuid: string): void => {
    const row = entityData.find(r => (r as any).uuid === uuid)
    if (row) handleDelete(row as unknown as Record<string, unknown>)
  }

  // Return
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 500,
            lineHeight: '1.3em',
            color: 'var(--forbrand-secondary)',
          }}
        >
          {GLOBAL_CONSTANTS.MT_ADMIN_ENTITY_TITLE}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Tooltip title="Download Excel">
            <IconButton size="medium" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Search">
            <IconButton size="medium" onClick={handleSearchOpen}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>
          {roleConfig.canEdit && (
            <>
              <Box
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--border-subtel)',
                }}
              />
              <CustomButton onClick={handleAdd}>
                {GLOBAL_CONSTANTS.MT_ADMIN_ENTITY_ADD}
              </CustomButton>
            </>
          )}
        </Box>
      </Box>
      <MTCommonTable
        columns={tableColumns}
        data={entityData}
        loading={loading}
        onRowEdit={roleConfig.canEdit ? handleRowEdit : undefined}
        onRowDelete={roleConfig.canEdit ? handleRowDelete : undefined}
        showFooter={true}
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <MTAdminEntityForm
        open={modalOpen}
        onClose={handleModalClose}
        editingEntity={editingEntity}
        onSubmit={handleFormSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onCancel={handleDeleteDialogClose}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this entity?"
        message="On deletion of entity, all the data pertaining to it would get delete. Do you want to continue ?"
        confirmText="Delete"
        itemType="entity"
        type="warning"
        showCancelButton={true}
      />

      <EntitySearch
        open={searchOpen}
        anchorEl={searchAnchorEl}
        onClose={handleSearchClose}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        fields={MT_ENTITY_COLUMNS.map(col => {
          if (col.id === 'countryName') {
            return {
              ...col,
              label: formatMTHeaderLabel(col.label),
              type: 'dropdown',
              placeholder: 'Select country',
              options: reduxCountryOptions.map(c => ({
                label: c.country_name,
                value: c.country_name,
              })),
            }
          }
          return buildMTSearchField(col)
        })}
        placement="bottom-start"
        searchCriteria={searchCriteria}
        setSearchCriteria={setSearchCriteria}
      />
    </Box>
  )
}
